import { t as tr } from '../i18n.js'
import { useEffect, useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { api, ApiError, type DirectoryUser } from '../api.js'

type Entry = { cidr: string; label: string | null }

/** Admin: per-account IP allowlist (max 5) and the restriction switch. */
export function IpAllowlistEditor({ user, onClose }: { user: DirectoryUser; onClose: () => void }) {
  const q = useQuery({ queryKey: ['ip-allowlist', user.id], queryFn: () => api<{ restricted: boolean; entries: Entry[] }>(`/users/${user.id}/ip-allowlist`) })
  const [restricted, setRestricted] = useState(false)
  const [entries, setEntries] = useState<Entry[]>([])
  const [err, setErr] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)
  useEffect(() => { if (q.data) { setRestricted(q.data.restricted); setEntries(q.data.entries) } }, [q.data])
  const save = useMutation({
    mutationFn: () => api<{ restricted: boolean; entries: Entry[] }>(`/users/${user.id}/ip-allowlist`, { method: 'PUT', json: { restricted, entries: entries.filter((e) => e.cidr.trim()) } }),
    onSuccess: (r) => { setRestricted(r.restricted); setEntries(r.entries); setSaved(true) },
    onError: (e) => setErr(e instanceof ApiError ? e.message : 'Could not save'),
  })
  const set = (i: number, patch: Partial<Entry>) => setEntries((es) => es.map((e, j) => (j === i ? { ...e, ...patch } : e)))
  return (
    <section className="card stack editor">
      <div className="row between"><h2 style={{ margin: 0 }}>Network restriction — {user.displayName}</h2><button className="link" onClick={onClose}>{tr("Close")}</button></div>
      <p className="small">When enabled, this account can sign in and stay signed in only from the listed addresses (up to five; IPv4/IPv6, single address or CIDR). Existing sessions from other addresses stop working immediately.</p>
      <label className="inline"><input type="checkbox" checked={restricted} onChange={(e) => setRestricted(e.target.checked)} /> Restrict this account to the addresses below</label>
      <table className="table">
        <thead><tr><th>{tr("Address / CIDR")}</th><th>{tr("Label")}</th><th></th></tr></thead>
        <tbody>
          {entries.map((e, i) => (
            <tr key={i}>
              <td><input className="mono" value={e.cidr} placeholder="203.0.113.7 or 10.0.0.0/24" onChange={(ev) => set(i, { cidr: ev.target.value })} /></td>
              <td><input value={e.label ?? ''} placeholder={tr("office, home…")} onChange={(ev) => set(i, { label: ev.target.value || null })} /></td>
              <td><button className="link danger" onClick={() => setEntries((es) => es.filter((_, j) => j !== i))}>Remove</button></td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="row">
        <button className="button" disabled={entries.length >= 5} onClick={() => setEntries((es) => [...es, { cidr: '', label: null }])}>+ Address</button>
        <button className="button primary" disabled={save.isPending} onClick={() => { setErr(null); setSaved(false); save.mutate() }}>Save</button>
      </div>
      {err && <p className="bad">{err}</p>}
      {saved && <p className="good">{tr("Saved.")}</p>}
    </section>
  )
}
